/**
 * Turnstile Cookie Manager - handles the cookie-based Turnstile verification flow.
 *
 * Responsibilities:
 * - POST /grants/verify-browser with Turnstile token to set HttpOnly cookie
 * - Proactive cookie refresh (4 min interval, 1 min before 5 min expiry)
 * - Response interceptor with retry queue for 403 TURNSTILE_COOKIE_MISSING/EXPIRED
 * - Explicit guard: never retry requests to /grants/verify-browser (prevents infinite loop)
 *
 * This is a pure utility (no React dependency) - can be used from TurnstileProvider
 * and apiClient.ts response interceptor.
 */
import type { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from "axios";
import * as Sentry from "@sentry/react";

/** Shape of the error response from TurnstileCookieGuard */
interface TurnstileErrorResponse {
  errorCode?: string;
}

/** Axios config extended with retry flag to prevent infinite retry loops */
interface RetriableAxiosConfig extends InternalAxiosRequestConfig {
  _retried?: boolean;
}

// ============================================================
// Constants
// ============================================================

/** Path to the browser verification endpoint */
export const VERIFY_BROWSER_PATH = "/grants/verify-browser";

/**
 * Cookie refresh interval in milliseconds.
 * Cookie has 5 min TTL; refresh at 4 min to avoid expiry during active use.
 */
export const COOKIE_REFRESH_INTERVAL_MS = 4 * 60 * 1000;

/**
 * Client-side companion cookie name.
 *
 * SECURITY NOTE: This cookie is a client-side hint ONLY. It does NOT provide
 * access or security enforcement. The HttpOnly JWT cookie verified by
 * TurnstileCookieGuard remains the sole enforcement point. This cookie can be
 * fabricated by any JS on the page — never trust it for security decisions.
 *
 * Purpose: avoid redundant POST /grants/verify-browser when opening new tabs
 * while the HttpOnly cookie from a previous tab is still valid.
 */
export const COMPANION_COOKIE_NAME = "grants-verified-hint";

/** Max-age for companion cookie in seconds (matches HttpOnly cookie TTL) */
export const COMPANION_COOKIE_MAX_AGE = 300;

/** Timeout (ms) waiting for widget reset before forcing full remount */
const RESET_TIMEOUT_MS = 5000;

/** Error codes returned by TurnstileCookieGuard on 403 */
const RETRYABLE_ERROR_CODES = [
  "TURNSTILE_COOKIE_MISSING",
  "TURNSTILE_COOKIE_EXPIRED",
] as const;

// ============================================================
// Companion cookie helpers
// ============================================================

/** Set the companion cookie via document.cookie */
function setCompanionCookie(): void {
  if (typeof document === "undefined") return;
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${COMPANION_COOKIE_NAME}=1; max-age=${COMPANION_COOKIE_MAX_AGE}; path=/; SameSite=Strict${secure}`;
}

/** Check if companion cookie exists (truthy = recently verified) */
function hasCompanionCookie(): boolean {
  if (typeof document === "undefined") return false;
  return document.cookie.split(";").some((c) => c.trim().startsWith(`${COMPANION_COOKIE_NAME}=`));
}

// ============================================================
// Types
// ============================================================

export interface TurnstileCookieManagerOptions {
  /** Axios instance to use for API calls */
  apiClient: AxiosInstance;
  /** Function to reset the Turnstile widget (triggers new challenge) */
  resetWidget: () => void;
  /** Callback when cookie is set (true) or invalidated (false) */
  onCookieReady: (ready: boolean) => void;
  /** Callback when verification or widget fails */
  onError: () => void;
  /** Function to force full remount of Turnstile widget (when reset() doesn't work) */
  forceRemount: () => void;
}

export interface TurnstileCookieManager {
  /** Verify browser by POSTing token to /grants/verify-browser */
  verifyBrowser: (token: string) => Promise<void>;
  /** Handle new token from widget (called by TurnstileProvider onSuccess) */
  handleNewToken: (token: string) => Promise<void>;
  /** Create an axios response error interceptor for 403 retry */
  createResponseInterceptor: () => (error: AxiosError) => Promise<any>;
  /** Check if cookie is currently set */
  isCookieReady: () => boolean;
  /** Timestamp of last successful verify-browser call */
  getLastVerifyTimestamp: () => number;
  /** Cleanup timers */
  destroy: () => void;
}

// ============================================================
// Factory
// ============================================================

/**
 * Creates a TurnstileCookieManager instance.
 *
 * Flow:
 * 1. TurnstileProvider onSuccess -> handleNewToken -> verifyBrowser -> cookie set
 * 2. On 403 -> response interceptor -> resetWidget() -> widget fires onSuccess ->
 *    handleNewToken -> verifyBrowser -> cookie set -> queued requests retried
 */
export function createTurnstileCookieManager(
  options: TurnstileCookieManagerOptions
): TurnstileCookieManager {
  const { apiClient, resetWidget, onCookieReady, onError, forceRemount } = options;

  let cookieReady = false;
  let refreshTimerId: ReturnType<typeof setTimeout> | null = null;
  let resetTimeoutId: ReturnType<typeof setTimeout> | null = null;
  let lastVerifyTimestamp = 0;

  // Re-challenge state: when a 403 triggers re-challenge, the interceptor
  // waits on this promise. It is resolved when handleNewToken successfully
  // verifies the browser again.
  let reChallengeResolve: (() => void) | null = null;
  let reChallengeReject: ((err: Error) => void) | null = null;
  let reChallengePromise: Promise<void> | null = null;
  let isRefreshing = false;

  // --------------------------------------------------------
  // Proactive refresh timer
  // --------------------------------------------------------

  function startRefreshTimer(): void {
    clearRefreshTimer();
    refreshTimerId = setTimeout(() => {
      Sentry.addBreadcrumb({ category: "turnstile", message: "proactive_refresh" });
      resetWidget();
      startResetTimeout();
    }, COOKIE_REFRESH_INTERVAL_MS);
  }

  function clearRefreshTimer(): void {
    if (refreshTimerId !== null) {
      clearTimeout(refreshTimerId);
      refreshTimerId = null;
    }
  }

  /** Safety net: if resetWidget() doesn't produce a token in time, force full remount */
  function startResetTimeout(): void {
    clearResetTimeout();
    resetTimeoutId = setTimeout(() => {
      Sentry.captureMessage("Turnstile reset timeout - forcing remount", {
        level: "warning",
        tags: { component: "turnstile" },
        extra: { lastVerifyTimestamp, msSinceLastVerify: Date.now() - lastVerifyTimestamp },
      });
      forceRemount();
    }, RESET_TIMEOUT_MS);
  }

  function clearResetTimeout(): void {
    if (resetTimeoutId !== null) {
      clearTimeout(resetTimeoutId);
      resetTimeoutId = null;
    }
  }

  // --------------------------------------------------------
  // Core verification
  // --------------------------------------------------------

  async function verifyBrowser(token: string): Promise<void> {
    if (!token) return;

    try {
      await apiClient.post(VERIFY_BROWSER_PATH, { token });
      cookieReady = true;
      lastVerifyTimestamp = Date.now();
      setCompanionCookie();
      clearResetTimeout();
      Sentry.addBreadcrumb({ category: "turnstile", message: "verify_browser_success" });
      onCookieReady(true);
      startRefreshTimer();

      // Resolve pending re-challenge if any
      if (reChallengeResolve) {
        reChallengeResolve();
        reChallengeResolve = null;
        reChallengeReject = null;
        reChallengePromise = null;
        isRefreshing = false;
      }
    } catch (err) {
      clearResetTimeout();
      Sentry.captureMessage("Turnstile verify-browser failed", {
        level: "warning",
        tags: { component: "turnstile" },
        extra: { error: err instanceof Error ? err.message : String(err) },
      });
      // Reject pending re-challenge if any
      if (reChallengeReject) {
        reChallengeReject(err instanceof Error ? err : new Error(String(err)));
        reChallengeResolve = null;
        reChallengeReject = null;
        reChallengePromise = null;
        isRefreshing = false;
      }
      onError();
    }
  }

  async function handleNewToken(token: string): Promise<void> {
    // On initial tab load (never verified in this tab), check companion cookie.
    // If a previous tab already verified, the HttpOnly cookie is set and we can skip the POST.
    // Proactive refresh (lastVerifyTimestamp > 0) always calls verifyBrowser.
    if (lastVerifyTimestamp === 0 && hasCompanionCookie()) {
      cookieReady = true;
      lastVerifyTimestamp = Date.now();
      Sentry.addBreadcrumb({ category: "turnstile", message: "companion_cookie_skip" });
      onCookieReady(true);
      startRefreshTimer();
      return;
    }
    await verifyBrowser(token);
  }

  // --------------------------------------------------------
  // Re-challenge flow (used by response interceptor)
  // --------------------------------------------------------

  /**
   * Start or join a pending re-challenge.
   *
   * The first 403 call creates a promise and calls resetWidget().
   * When the Turnstile widget resolves, TurnstileProvider calls handleNewToken,
   * which calls verifyBrowser, which resolves the promise.
   * Subsequent 403s during the same window join the same promise.
   */
  function getOrStartReChallenge(): Promise<void> {
    if (isRefreshing && reChallengePromise) {
      return reChallengePromise;
    }

    isRefreshing = true;
    cookieReady = false;

    reChallengePromise = new Promise<void>((resolve, reject) => {
      reChallengeResolve = resolve;
      reChallengeReject = reject;
    });

    Sentry.addBreadcrumb({ category: "turnstile", message: "re_challenge_started" });
    resetWidget();
    startResetTimeout();

    return reChallengePromise;
  }

  // --------------------------------------------------------
  // Response interceptor
  // --------------------------------------------------------

  function createResponseInterceptor() {
    return async (error: AxiosError): Promise<any> => {
      const status = error.response?.status;
      const errorCode = (error.response?.data as TurnstileErrorResponse)?.errorCode;
      const requestUrl = error.config?.url ?? "";

      // Only handle 403 with specific Turnstile error codes
      if (
        status !== 403 ||
        !errorCode ||
        !(RETRYABLE_ERROR_CODES as readonly string[]).includes(errorCode)
      ) {
        return Promise.reject(error);
      }

      // NEVER retry requests to verify-browser (prevents infinite loop)
      if (requestUrl.includes(VERIFY_BROWSER_PATH)) {
        return Promise.reject(error);
      }

      // Don't retry already-retried requests
      if ((error.config as RetriableAxiosConfig | undefined)?._retried) {
        return Promise.reject(error);
      }

      try {
        // Join or start re-challenge
        await getOrStartReChallenge();

        // Mark request as retried and replay it
        (error.config as RetriableAxiosConfig)._retried = true;
        return apiClient.request(error.config!);
      } catch {
        return Promise.reject(error);
      }
    };
  }

  // --------------------------------------------------------
  // Public API
  // --------------------------------------------------------

  return {
    verifyBrowser,
    handleNewToken,
    createResponseInterceptor,
    isCookieReady: () => cookieReady,
    getLastVerifyTimestamp: () => lastVerifyTimestamp,
    destroy: () => {
      clearRefreshTimer();
      clearResetTimeout();
      if (reChallengeReject) {
        reChallengeReject(new Error("Manager destroyed"));
      }
      reChallengeResolve = null;
      reChallengeReject = null;
      reChallengePromise = null;
      isRefreshing = false;
    },
  };
}
